/*
  Monogram Shop / Personalization Scripts

  Functions:
  Helpers:
    # trySmallerFontSize
      @param {size}
        Recursive fn that handles testing the font size when text is typed. An svg-tester is created, which calculates the imprint width and height from inches to pxs and convert the font size from pts to pxs.

    # setColor
      @param {e} - click event
        After click, we take the click targets dataset variables `hex` and `color` to fill in the live preview modal's text color with the selected radio button.

    # setFont
      @param {e} - click event
        After click, we take the click targets dataset variables `cssname` (renamed as `font`), `title`, `placementsizes` and sets the font on the live preview modal. We also set the font in `svg-tester` to test for the text fitting inside the imprint width and height.

    # setPlacement
      @param {e} - click event
        Disabled. Products currently have one placement per product.

    # updateText
      @param {e} - event = keydown event
        After typing text, we find the `personalization-text` tag and update the inner text with the `target.value`. With each update, we run the `testFontSize` function.

    # testFontSize
      This function calculates the imprint width and height, converting inches to pxs and setting that parameter on the svg-tester. We have a few helper fns to calculate the min and max font sizes from pts to pxs; adjust the width of the anchored imprint size; and fn to handle adding to cart on the personalization ATC button.

    # window.renderPersonalizationModal
      This function renders the live preview modal. We loop through the `options` array to set the global fonts, colors, placements stored in the shop metaobjects.
*/

// Defaults — Initial State
let currentPlacement = "center-back";
let currentFont = "Bold Serif";
let currentHex = "#000000";
let currentFontFamily = "Playfair Display";
let charLimit = 15;
let fontSizes = {};
const currentTextSize = 110;
let imprintWidth = 7.5;
let imprintHeight = 5.25;
let maxFontSize = 110;
let minFontSize = 72;
let upcharge = "$6";
let personalizationImage = "";
let personalizationImageAltText = "";

let allPlacements = `
  <text
    id="center-back"
    font-size="46px"
    data-maxlength="15"
    data-placeholder="Center Back"
    font-family="Sans Serif"
    fill="#dfc9bf"
    data-basefont="46"
    style="transform: matrix(1 0 0 1 500 513)"
    class="active-placement personalization-text"
    text-anchor="middle"
  >
    Center back
  </text>
`;

// For Jest Tests
function handleize(str) {
  return str.toLowerCase().replace(/\W+/g, "-");
}

const updateFontOptionsWithSizes = (
  fontOptionsArray,
  fontSizesArray,
  productPlacement,
) => {
  const handleizedPlacement = productPlacement
    .toLowerCase()
    .replace(/\W+/g, "-");

  return fontOptionsArray.map((fontOption) => {
    // Find the matching fontSize object
    const foundFontSize = fontSizesArray.find((fontSize) => {
      const foundFont = fontSize.font.find(
        (font) => font.title === fontOption.title,
      );
      const foundPlacement = fontSize.placement.find(
        (placement) => placement.title === productPlacement,
      );
      return foundFont && foundPlacement;
    });

    if (foundFontSize) {
      // Create a new object with the updated font_sizes
      const updatedFontSizes = {
        ...fontOption.font_sizes,
        [handleizedPlacement]: {
          min: foundFontSize.minSize,
          max: foundFontSize.maxSize,
        },
      };
      // Return the updated font option object
      return {
        ...fontOption,
        font_sizes: updatedFontSizes,
      };
    }

    // Return the font option unchanged if no match was found
    return fontOption;
  });
};

const buildFontSizesArray = (className) =>
  Array.from(document.querySelectorAll(className)).map(
    ({ dataset: { maxSize, minSize, font, placement } }) => ({
      maxSize,
      minSize,
      font: JSON.parse(font),
      placement: JSON.parse(placement),
    }),
  );

const renderPersonalizationData = () => {
  // Get metaobject information from hidden DOM element
  const personalizationData = document.getElementById(
    "personalization-data-container",
  ).dataset;
  let { colors, fonts, placements, personalizationPlacement } =
    personalizationData;
  const { admin: isAdmin } = personalizationData;

  // Parse the relevant fields
  colors = JSON.parse(colors);
  fonts = JSON.parse(fonts);
  placements = JSON.parse(placements);
  personalizationPlacement = JSON.parse(personalizationPlacement);

  // Build the fontSizesArray
  const fontSizesArray = buildFontSizesArray(".personalization-font-size");
  const productPlacement = personalizationPlacement.title;
  const placementsArray = placements.find(
    (placement) => placement.title === productPlacement,
  );
  const colorOptionsArray = colors;

  const fontOptionsArray = updateFontOptionsWithSizes(
    fonts,
    fontSizesArray,
    productPlacement,
  );

  const options = {
    type: {
      options: ["Embroidery"],
      lineProperty: "CustomizationType",
      fieldName: "Personalization Type",
      charRule: "Chars",
      charlimit: 10,
    },
    placement: {
      options: [placementsArray.title],
      lineProperty: "CustomizationPlacement",
      fieldName: "Placement",
      placements: [placementsArray],
    },
    color: {
      options: colorOptionsArray,
      lineProperty: "CustomizationColor",
      fieldName: "Embroidery",
    },
    font: {
      options: fontOptionsArray,
      lineProperty: "CustomizationFont",
      fieldName: "Font",
    },
  };
  return {
    isAdmin,
    options,
    personalizationPlacement,
    fontOptionsArray,
  };
};

export const updateText = (e) => {
  const text = e.currentTarget;
  const update = text.value;
  document.querySelectorAll(".personalization-text").forEach((place) => {
    const newText = update.length > 0 ? update : place.dataset.placeholder;
    // eslint-disable-next-line no-param-reassign
    place.textContent = newText;
    document.getElementById("svg-tester").textContent = newText;
  });
};

export const trySmallerFontSize = (size) => {
  const tester = document.querySelector("#svg-tester");
  const activePlacement = document.querySelector(".active-placement");
  const { impwidth, impheight, max } = tester.dataset;

  // converting from inches to pixels
  const impWidthPx = impwidth * 96;
  const impHeightPx = impheight * 96;
  tester.style.fontSize = `${size}px`;

  if (
    tester.getBBox().width > impWidthPx ||
    tester.getBBox().height > impHeightPx
  ) {
    trySmallerFontSize(size - 1);
  } else {
    const pts = Math.floor((size * 3) / 4);
    const { basemax, basefont } = activePlacement.dataset;
    const sizeRatio = size / ((max * 4) / 3);
    const newFontSize = `${basefont * sizeRatio * (max / basemax)}px`;

    tester.dataset.current = pts;
    tester.style.fontSize = `${(pts * 4) / 3}px`;
    activePlacement.style.fontSize = newFontSize;

    document.querySelectorAll(".personalizer-font").forEach((fontElement) => {
      if (fontElement.checked) {
        // eslint-disable-next-line no-param-reassign
        fontElement.dataset.currentsize = `${pts}pt`;
      }
    });
  }
};

export const testFontSize = () => {
  const { fontOptionsArray } = renderPersonalizationData();
  const svg = document.getElementById("live-preview-svg");
  const svgWidth = svg.parentNode.offsetWidth;
  const tester = document.getElementById("svg-tester");
  const activeFont = tester.dataset.font;
  const fontAdjustment =
    fontOptionsArray.find((opt) => opt.font === activeFont)?.width_adjustment ||
    1;
  const impWidth = parseFloat(tester.dataset.impwidth);
  const impHeight = parseFloat(tester.dataset.impheight);

  tester.style.fontSize = `${(tester.dataset.max * 4) / 3}px`;
  const maxWidth = (tester.getBBox().width / 96) * fontAdjustment;
  const maxHeight = tester.getBBox().height / 96;

  tester.style.fontSize = `${(tester.dataset.min * 4) / 3}px`;
  const minWidth = (tester.getBBox().width / 96) * fontAdjustment;
  const minHeight = tester.getBBox().height / 96;

  const activePlacement = document.querySelector(".active-placement");
  const baseMin = parseFloat(activePlacement.dataset.basefont);
  const baseMax = parseFloat(activePlacement.dataset.basemax);
  maxFontSize = parseFloat(tester.dataset.max);
  minFontSize = parseFloat(tester.dataset.min);

  const setMaxFontSize = () => {
    tester.style.fontSize = `${(maxFontSize * 4) / 3}px`;
    tester.dataset.current = maxFontSize;
    activePlacement.style.fontSize = `${baseMin * (maxFontSize / baseMax)}px`;
    tester.dataset.current = maxFontSize;

    document.querySelectorAll(".personalizer-font").forEach((font) => {
      if (font.checked) {
        // eslint-disable-next-line no-param-reassign
        font.dataset.currentsize = `${maxFontSize}pt`;
      }
    });

    document
      .getElementById("personalizer-input")
      .setAttribute("maxlength", activePlacement.dataset.maxlength);
  };

  const setMinFontSize = () => {
    const size = Math.floor((minFontSize * 4) / 3);
    const sizeRatio = size / ((maxFontSize * 4) / 3);
    activePlacement.style.fontSize = `${baseMin * sizeRatio * (maxFontSize / baseMax)}px`;
    document
      .getElementById("personalizer-input")
      .classList.add("check-validity");
    document
      .getElementById("personalization-text-errors")
      .classList.remove("hidden");
    document
      .getElementById("personalization-text-subcopy")
      .classList.add("hidden");
    document
      .getElementById("personalizer-input")
      .setAttribute(
        "maxlength",
        document.getElementById("personalizer-input").value.length,
      );
  };

  const adjustAnchor = () => {
    const liveWidth = activePlacement.getComputedTextLength();
    const liveBox = activePlacement.getBBox().width;
    const adjBox = (((liveBox - liveWidth) * svgWidth) / 1000) * -1;
    activePlacement.setAttribute("x", adjBox);
  };

  const addATCEvent = () => {
    const atcButton = document.getElementById("personalization-atc");
    atcButton.removeEventListener("click", window.atcClick);
    atcButton.addEventListener("click", window.atcClick);
    atcButton.innerText = "Add to Bag";
  };

  if (maxWidth < impWidth && maxHeight < impHeight) {
    addATCEvent();
    document
      .getElementById("personalizer-input")
      .classList.remove("check-validity");
    document
      .getElementById("personalization-text-errors")
      .classList.add("hidden");
    document
      .getElementById("personalization-text-subcopy")
      .classList.remove("hidden");
    setMaxFontSize();
    if (activePlacement.classList.contains("anchor-middle")) {
      adjustAnchor();
    }
  } else if (minHeight > impHeight && minWidth > impWidth) {
    setMinFontSize();
    if (activePlacement.classList.contains("anchor-middle")) {
      adjustAnchor();
    }
  } else {
    addATCEvent();
    document
      .getElementById("personalizer-input")
      .setAttribute("maxlength", activePlacement.dataset.maxlength);
    const wRatio = impWidth / maxWidth;
    const hRatio = impHeight / maxHeight;
    const calcRatio = Math.min(wRatio, hRatio, 1);
    const size = Math.floor(((maxFontSize * 4) / 3) * calcRatio);
    const sizeRatio = size / ((maxFontSize * 4) / 3);
    tester.style.fontSize = `${size}px`;
    document
      .getElementById("personalizer-input")
      .classList.remove("check-validity");
    document
      .getElementById("personalization-text-errors")
      .classList.add("hidden");
    document
      .getElementById("personalization-text-subcopy")
      .classList.remove("hidden");

    if (tester.getBBox().width > impWidth * 96) {
      trySmallerFontSize(size - 1);
    } else {
      const pts = Math.floor((size * 3) / 4);
      tester.dataset.current = pts;
      activePlacement.style.fontSize = `${baseMin * sizeRatio * (maxFontSize / baseMax)}px`;
      document.querySelectorAll(".personalizer-font").forEach((font) => {
        // eslint-disable-next-line no-param-reassign
        if (font.checked) font.dataset.currentsize = `${pts}pt`;
      });
      if (activePlacement.classList.contains("anchor-middle")) {
        adjustAnchor();
      }
    }
  }
};

export const setColor = (e) => {
  const { hex, color } = e.currentTarget.dataset;
  document.querySelectorAll(".personalization-text").forEach((p) => {
    // eslint-disable-next-line no-param-reassign
    p.style.fill = hex;
  });
  document.getElementById("personalization-color").innerText = color;
};

export const setFont = (e) => {
  const button = e.currentTarget;
  const { cssname: font, title, placementsizes } = button.dataset;
  currentFontFamily = font;

  document.querySelectorAll(".personalization-text").forEach((p) => {
    // eslint-disable-next-line no-param-reassign
    p.style.fontFamily = font;
  });

  const svgTester = document.getElementById("svg-tester");
  svgTester.style.fontFamily = font;
  svgTester.dataset.font = font;

  document.getElementById("personalization-font").innerText = title;

  if (placementsizes) {
    const selectedFontSizes = JSON.parse(placementsizes);
    const place = handleize(currentPlacement);
    if (selectedFontSizes[place]) {
      const { max, min } = selectedFontSizes[currentPlacement];
      svgTester.dataset.max = max;
      svgTester.dataset.min = min;
    }
  }

  currentFont = button.id;
  // testFontSize();
};

export const setPlacement = (e) => {
  const thePlacement = handleize(e.currentTarget.dataset.placement);
  currentPlacement = thePlacement;
  const svgTester = document.getElementById("svg-tester");

  document.querySelectorAll(".personalization-text").forEach((text) => {
    const isActive = text.id === `text-placement-${thePlacement}`;
    text.classList.toggle("hidden", !isActive);
    text.classList.toggle("active-placement", isActive);

    if (isActive) {
      const { impwidth, impheight } = text.dataset;
      svgTester.dataset.impwidth = impwidth;
      svgTester.dataset.impheight = impheight;

      if (fontSizes[thePlacement]) {
        const { max, min } = fontSizes[thePlacement];
        svgTester.dataset.max = max;
        svgTester.dataset.min = min;
      }
    }
  });

  testFontSize();
};

export const renderPersonalizationModal = (e) => {
  e.preventDefault();
  const { options, is_admin: isAdmin } = renderPersonalizationData();
  let type = ``;
  let placement = ``;
  let color = ``;
  let font = ``;
  let optionIdx = 0;
  if (
    options.placement &&
    options.placement.placements &&
    options.placement.placements.length > 1
  ) {
    optionIdx += 1;
  }
  let fontTesters = ``;

  // Currently disabled. Only embroidery option
  if (options.type) {
    const typeOptions = options.type;
    if (typeOptions.options) {
      typeOptions.options.forEach((option) => {
        type = `
          <fieldset class="hidden">
            <legend class="my-4 uppercase small font-semibold">Personalization Type</legend>
            <div class="pl-7">
              <label class="uppercase font-semibold">
                <input checked type="radio" class="m-auto text-[1rem] personalizer-type personalization-radio line-prop-input" data-key="CustomizationType" value="${option}" name="PROPERTIES[CustomizationType]">
                ${option}
              </label>
            </div>
          </fieldset>
        `;
      });
    }
  }

  // Render color options
  if (options.color) {
    const colorOption = options.color;
    let colors = ``;
    colorOption.options.forEach((option, index) => {
      // if hex code is white, adjust to a light grey color to show up on the live preview modal.
      const colorStyle = `background-color: ${option.code.includes("#fff") ? "#eee" : option.code}`;
      let selected = ``;
      const optionValue = handleize(option.title);

      if (index === 0) {
        selected = `checked`;
        currentHex = option.code;
      }

      colors += `
        <input ${selected}
          id="personalization-color-${optionValue}"
          type="radio"
          class="m-auto hidden-radio product-circle-radio standard personalizer-type personalization-radio line-prop-radio personalization-color-radio"
          data-key="${colorOption.lineProperty}"
          data-hex="${option.code}"
          data-color="${option.title}"
          data-type="${option.type[0]}"
          value="${option.title} (${option.thread})"
          name="PROPERTIES[${colorOption.lineProperty}]"
        />
        <label
          data-color="${option.title}" for="personalization-color-${optionValue}"
          class="product-circle-radio-label text-center pointer personalization-color-label"
          aria-label="Color: ${option.title}"
          style="${colorStyle}"
        >
        </label>
      `;
    });

    let noDisplay = ``;
    if (colorOption.options.length <= 1) noDisplay = `hidden`;
    else optionIdx += 1;

    color = `
      <fieldset class="${noDisplay}">
        <legend>
          <span class="font-semibold">
            <span class="rounded-[50%] h-6 w-6 bg-core-darkCream inline-flex justify-center items-center font-semibold font-display">
            ${optionIdx}
            </span>
            <span class="uppercase">${colorOption.fieldName}:</span>
          </span>
          <span data-color="${colorOption.options[0].title}" id="personalization-color">${colorOption.options[0].title}</span>
        </legend>
        <div class="color-container pl-7 mt-2">
          ${colors}
        </div>
      </fieldset>
    `;
  }

  // Render font options
  if (options.font) {
    const fontOption = options.font;
    const fontIdx = 0;
    const hasHiddenFonts = fontOption.options.find(
      (opt) => opt.enabled !== true,
    );
    let fonts = ``;

    fontOption.options.forEach((option, index) => {
      let placeSizes = ``;
      let selected = ``;
      if (index === fontIdx) {
        selected = `checked`;
        currentFont = option.font_handle;
        currentFontFamily = option.font;
        fontSizes = option.font_sizes;
      }
      fontTesters += `
        <text
          class="personalization-font-tester"
          text-anchor="middle"
          visibility="hidden"
          opacity="0"
          font-size="30px"
          fill="#464646"
          font-family="${option.font}"
        >
          TEST FONT
        </text>
      `;
      if (option.font_sizes) {
        placeSizes = `data-placementsizes='${JSON.stringify(option.font_sizes)}'`;
      }

      // Adds admin-only styles to the font buttons, so we know what is live and not live.
      let enableButtonStyle = ``;
      if (isAdmin) {
        enableButtonStyle =
          option.enabled !== true
            ? "border-brand-darkerMocha bg-brand-info/25"
            : "";
      } else {
        enableButtonStyle = option.enabled !== true ? "!hidden" : "";
      }

      fonts += `
        <input ${selected} ${placeSizes}
          id="personalization-font-${option.font_handle}"
          type="radio"
          class="personalizer-type personalization-radio line-prop-radio personalizer-font product-radio personalization-font-radio m-auto font-[1rem] hidden-radio"
          data-sizekey="CustomizationFontSize"
          data-currentsize=""
          data-key="${fontOption.lineProperty}"
          value="${option.font}"
          data-cssname="${option.font}"
          data-title="${option.title}"
          name="PROPERTIES[${fontOption.lineProperty}]"
        >
        <label
          data-font="${option.font}"
          for="personalization-font-${option.font_handle}"
          class="product-radio-label personalization-font-label button button-secondary normal-case text-center !m-0 ${enableButtonStyle}"
          style="font-family: ${option.font}"
        >
          ${option.title}
        </label>
      `;
    });
    let noDisplay = ``;
    if (fontOption.options.length <= 1) {
      noDisplay = `hidden`;
    } else {
      optionIdx += 1;
    }
    font = `
      <fieldset class="${noDisplay}">
        <legend>
        <span class="font-semibold">
          <span class="rounded-[50%] h-6 w-6 bg-core-darkCream inline-flex justify-center items-center font-semibold uppercase font-display">
          ${optionIdx}
          </span>
          <span class="uppercase">${options.font.fieldName}:</span>
        </span>
          <span data-font="Sans Serif" id="personalization-font">Sans Serif</span>
        </legend>
        <div class="fonts-container grid grid-cols-2 md:flex md:flex-wrap pl-7 gap-2 mt-2">
        <div class="admin-view ${isAdmin && hasHiddenFonts ? "block " : "hidden"} mb-4">
          <p class="m-0 font-semibold uppercase text-brand-errorText">Admin View</p>
          <p class="m-0 text-sm italic">
            Disabled fonts will appear in a light blue button.
          </p>
        </div>

          ${fonts}
        </div>
      </fieldset>
    `;
  }

  // Find the font index to know which font in the array will be associated with the maxFontSize
  const fontIdx = options.font.options.findIndex(
    (opt) => opt.font === currentFontFamily,
  );

  if (options.placement) {
    let noDisplay = ``;
    const placementOptions = options.placement;
    if (placementOptions.placements) {
      if (placementOptions.placements.length <= 1) {
        noDisplay = `hidden`;
      }
    }
    let placements = ``;
    if (placementOptions.placements) {
      allPlacements = ``;
      placementOptions.placements.forEach((option, index) => {
        let selected = ``;
        let visible = `hidden`;
        let active = "";
        if (index === 0) {
          selected = `checked`;
          visible = "";
          active = "active-placement";
          currentPlacement = handleize(option.title);
          charLimit = option.max_char;
          imprintWidth = option.max_width;
          imprintHeight = option.max_height;
        }
        maxFontSize =
          options.font.options[fontIdx].font_sizes[currentPlacement].max;

        allPlacements += `
            <g style="transform: matrix(1, 0, 0, 1, ${option.matrix_x}, ${option.matrix_y}) rotate(${option?.rotation ? option.rotation : 0}deg)">
              <text
                id="text-placement-${handleize(option.title)}"
                data-impwidth="${option.max_width}"
                data-impheight="${option.max_height}"
                data-maxlength="${option.max_char}"
                data-placeholder="${option.placeholder}"
                data-basefont="${option.base_min}"
                data-basemax="${option.base_max}"
                data-font-family="${currentFontFamily}"
                class="personalization-text anchor-middle ${active} ${visible}"
                text-anchor="middle"
                font-size="${option.base_min * (maxFontSize / option.base_max)}px"
                fill="${currentHex}"
                font-family="${currentFontFamily}"
              >
                ${option.placeholder}
              </text>
            </g>
          `;
        placements += `
            <input ${selected}
              id="personalization-placement-${handleize(option.title)}"
              type="radio"
              class="m-auto text-[1rem] personalization-placement-radio personalizer-type personalization-radio line-prop-radio product-radio hidden-radio"
              data-key="${placementOptions.lineProperty}"
              value="${option.title}"
              data-placement="${option.title}"
              name="PROPERTIES[${placementOptions.lineProperty}]"
            >
            <label for="personalization-placement-${handleize(option.title)}" class="product-radio-label button button-secondary text-center normal-case">
              ${option.title}
            </label>
          `;
      });
    } else {
      placementOptions.options.forEach((option, index) => {
        let selected = ``;
        if (index === 0) {
          selected = `checked`;
          currentPlacement = handleize(option);
        }
        placements += `
          <input ${selected}
            id="personalization-placement-${handleize(option)}"
            type="radio"
            class="m-auto text-[1rem] personalization-placement-radio personalizer-type personalization-radio line-prop-radio product-radio hidden-radio"
            data-key="${placementOptions.lineProperty}"
            value="${option}"
            data-placement="${option}"
            name="PROPERTIES[${placementOptions.lineProperty}]"
          >
          <label for="personalization-placement-${handleize(option)}" class="product-radio-label button button-secondary text-center normal-case">
           ${option}
          </label>
        `;
      });
    }
    placement = `
      <fieldset class="${noDisplay} mb-8">
      <legend class="uppercase font-semibold my-4"><span class="rounded-[50%] h-6 w-6 bg-core-darkCream inline-flex justify-center items-center font-semibold uppercase font-display">1</span> ${placementOptions.fieldName}</legend>
      <div class="flex flex-wrap pl-7">
      ${placements}
      </div>
      </fieldset>
      `;
  }

  optionIdx += 1;

  const form = `
    ${type}
    ${placement}
    ${color}
    ${font}
    <span class="rounded-[50%] h-6 w-6 bg-core-darkCream inline-flex justify-center items-center font-semibold uppercase font-display">${optionIdx}</span>
    <label for="personalizer-input" id="personalizer-label">
      <span class="uppercase font-semibold">TEXT</span> (Up to <span id="input-1-char-limit">${charLimit} character${charLimit > 1 ? "s" : ""}</span>)
    </label>
    <div class="max-w-full pl-7">
      <input
        id="personalizer-input"
        data-case="mixed"
        data-verify="Chars"
        pattern="[A-Za-z][A-Za-z ]{0,25}"
        maxlength="${charLimit}"
        type="text"
        class="block m-auto w-[calc(100%-1rem)] standard mt-4 personalizer leading-5 line-prop-input p-1.5 !mb-1 placeholder-spice placeholder-pl-1 placeholder-text-sm placeholder-font-semibold focus:invalid:border focus:invalid:border-solid focus:invalid:border-spice focus:invalid:rounded-[4px] focus:invalid:!pb-2"
        data-key="Customization"
        name="PROPERTIES[Customization]"
        required
      >
      <div
        id="personalization-text-errors"
        class="hidden text-xs md:text-sm text-brand-errorText"
      >
        Oh snap! It looks like your text doesn’t fit. Another font might work better. Or, try adjusting the capitalization. If that doesn’t work, try something with fewer characters.
      </div>
      <div id="personalization-text-subcopy" class="text-xs md:text-sm">
        We use exactly what you type here, so please double-check the spelling!
        <strong>Note:</strong> Personalized items take an additional ${window.timelines.personalization.timeline} business days and are non-returnable.
      </div>
    </div>
  `;

  if (document.querySelector(".personalized").innerText) {
    upcharge = document.querySelector(".personalized").innerText.split("(");
    upcharge =
      upcharge.length > 1 ? `(${upcharge[1].replaceAll(")", "")})` : "";
  }

  if (document.getElementById("personalization-image")) {
    personalizationImage = document.getElementById("personalization-image")
      .dataset.image;
    personalizationImageAltText = document.getElementById(
      "personalization-image",
    ).dataset.altText;
  }

  const imageBox = `
    <div class="personalization-modal-container p-4 md:p-8 md:border md:border-solid md:border-brand-darkerMocha md:rounded-sm">
      <div class="hidden md:block mb-4">
        <h2 class="text-center">Personalization – ${options.type.options[0]} ${upcharge}</h2>
      </div>
      <div class="block md:hidden">
        <h2 class="text-left">Personalization</h2>
        <h3 class="text-left">${options.type.options[0]} ${upcharge}</h3>
      </div>
      <hr class="border-b border-t-0 boder-solid border-brand-deepMocha-75">
      <div class="grid md:grid-cols-2 w-full gap-4 md:gap-8 top-8">
        <div class="live-preview relative w-full">
          <div class="absolute w-full h-full">
            <svg id="live-preview-svg" viewBox="0 0 1000 1000" aria-hidden="true">
              ${fontTesters}
              <text id="svg-tester" y="-300" visibility="visible" opacity="1"></text>
              ${allPlacements}
            </svg>
          </div>
          <img class="live-preview-image aspect-square object-cover object-center" src='${personalizationImage}' alt-text='${personalizationImageAltText}'>
        </div>
        <div class="personalization-options space-y-4">
          ${form}
          <div class="button-container flex justify-center items-center mt-4 w-[calc(100%-1rem)] pl-7">
            <button id="personalization-atc" aria-label="Add to Bag" class="button button-primary w-full" data-final-sale="true">
              Add to Bag
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  window.buildModal(imageBox);
  maxFontSize = options.font.options[fontIdx].font_sizes[currentPlacement].max;
  minFontSize = options.font.options[fontIdx].font_sizes[currentPlacement].min;
  const textTester = document.getElementById("svg-tester");
  textTester.dataset.current = currentTextSize;
  textTester.dataset.font = currentFont;
  textTester.dataset.impwidth = imprintWidth;
  textTester.dataset.impheight = imprintHeight;
  textTester.dataset.max = maxFontSize;
  textTester.dataset.min = minFontSize;
  textTester.dataset.fontFamily = currentFontFamily;
  textTester.dataset.font = currentFont;
  textTester.style.fontSize = `${(maxFontSize * 4) / 3}px`;
  textTester.style.fontFamily = currentFont;
  textTester.style.fontFamily = currentFontFamily;
  document
    .getElementById("personalizer-input")
    .addEventListener("input", updateText);
  document
    .getElementById("personalizer-input")
    .addEventListener("input", testFontSize);
  document
    .getElementById("personalizer-input")
    .addEventListener("change", window.cleanInput);
  document
    .getElementById("personalizer-input")
    .addEventListener("input", window.cleanInput);
  document.querySelectorAll(".personalization-font-radio").forEach((input) => {
    input.addEventListener("input", setFont);
    input.addEventListener("change", setFont);
    input.addEventListener("input", testFontSize);
    input.addEventListener("change", testFontSize);
  });
  document.querySelectorAll(".personalization-color-radio").forEach((input) => {
    input.addEventListener("input", setColor);
    input.addEventListener("change", setColor);
  });
  document
    .querySelectorAll(".personalization-placement-radio")
    .forEach((input) => {
      input.addEventListener("input", setPlacement);
      input.addEventListener("change", setPlacement);
    });
  document
    .getElementById("personalization-atc")
    .addEventListener("click", window.atcClick);
  if (fontSizes[`${currentPlacement}`]) {
    document.querySelectorAll(".personalizer-font").forEach((fontElement) => {
      if (fontElement.checked) {
        // eslint-disable-next-line no-param-reassign
        fontElement.dataset.currentsize = `${fontSizes[currentPlacement].max}pt`;
      }
    });
  }
};

// Add event listener to preview modal button in admin-view if there is a placement selected and the account is an admin-account
const adminPreviewBtn = document.querySelector(".preview-modal-btn.admin-only");
if (adminPreviewBtn)
  adminPreviewBtn.addEventListener("click", renderPersonalizationModal);

window.testFontSize = testFontSize;
window.renderPersonalizationModal = renderPersonalizationModal;
